import React from 'react';

import delv from 'dlv';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { formatDataQaIds } from 'utils';

import IconCheck from 'components/icons/check/customizable-check';
import Picture from 'components/picture';
import NullImageFallBack from 'components/picture/null-picture-fallback';
import { Calories } from 'components/price-and-calories';
import { HiddenInput } from 'components/selection-indicator/indicator';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { IComboSlotOptionProps } from '../types';

export const ComboSlotOptionWrapper = styled.div`
  color: ${Styles.color.black};
  display: flex;
  flex-direction: column;
  flex-basis: 50%;
  padding: 0.3rem;
  outline: ${Styles.color.tertiary};
  overflow: hidden; // fixes image border radius bug
  width: 167px;

  ${Styles.desktop`
    flex-basis: 33%;
  `}
`;

interface IComboSlotOptionLabel {
  checked: boolean;
}

export const ComboSlotOptionLabel = styled.label<IComboSlotOptionLabel>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0;
  margin: 0;
  padding: 10px;
  height: 167px;
  background-color: ${Styles.color.cardBackground};
  border: 2.5px solid ${props => (props.checked ? Styles.color.primary : 'transparent')};
  border-radius: ${Styles.borderRadius};
  transition: border 0.3s;

  &:not([disabled]):hover {
    cursor: pointer;
    ${p =>
      p.checked
        ? css``
        : css`
            border-color: ${Styles.color.grey.four};
          `}
  }
`;

export const StyledPicture = styled(Picture)`
  height: 91px;
`;

export const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 167px;
`;

export const ComboSlotOptionCardDetail = styled.div`
  border-radius: ${Styles.borderRadius};
  display: flex;
  flex: 9;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  padding: 1.25rem;
  text-align: center;
`;

export const OptionTitle = styled.h4`
  margin: 0;
  padding: 0;
  font-family: ${Styles.fontFamily.body};
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: -0.23px;
  line-height: 1.07;
`;

export const OptionNutrition = styled.h5`
  margin: 0;
  padding: 5px 0 0 0;
  font-family: ${Styles.fontFamily.body};
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: -0.23px;
  line-height: 1.07;
`;

export const CheckContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 13px;
  height: 12px;
`;

const ComboSlotOption: React.FC<IComboSlotOptionProps> = ({
  option,
  item,
  isReadOnly,
  onChange,
  comboSlot,
  respectMaximum = false,
}) => {
  const { formatMessage } = useIntl();
  const formatCalories = useFormatCalories();

  const handleSelectionChange = () => {
    onChange(respectMaximum);
  };

  const checked = !!item;
  const hasImage = option.image && option.image.asset;
  const isEditable = !isReadOnly;

  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const formatJoules = useFormatJoules();
  // If a user increments the quantity of this combo slot,
  // we need to legally show the caloric intake for the amount
  // they are ordering. thus multiplying it by the quantity
  const calories: number = delv(option, 'nutrition.calories', 0) * (item ? item.quantity : 1);

  const caloriesInKCal = formatCalories(calories);
  const caloriesInJoules = formatJoules(calories);

  const euFormattedNutritionText = formatMessage(
    { id: 'euNutritionValues' },
    { calories: caloriesInKCal, joules: caloriesInJoules }
  );

  const globalizedCalorieFormat = calories ? `${euFormattedNutritionText}` : '';

  return (
    <ComboSlotOptionWrapper>
      <ComboSlotOptionLabel checked={checked}>
        {/* Don't show placeholder image if the item is read only and doesn't have an image */}
        {(hasImage || isEditable) && (
          <ImageContainer>
            {hasImage ? (
              <StyledPicture image={option.image} alt={option.name?.locale} objectFitContain />
            ) : (
              <NullImageFallBack />
            )}
          </ImageContainer>
        )}
        {isEditable && (
          <CheckContainer
            data-testid={`combo-slot-${formatDataQaIds(delv(option, 'name.locale', ''))}`}
          >
            <HiddenInput
              type="radio"
              name={comboSlot}
              checked={checked}
              disabled={isReadOnly}
              onChange={handleSelectionChange}
            />
            {checked && <IconCheck width="100%" height="100%" stroke={Styles.color.primary} />}
          </CheckContainer>
        )}
      </ComboSlotOptionLabel>

      <ComboSlotOptionCardDetail>
        <OptionTitle>{option.name?.locale}</OptionTitle>
        <OptionNutrition>
          <Calories>{enableEUNutritionDisplay ? globalizedCalorieFormat : caloriesInKCal}</Calories>
        </OptionNutrition>
      </ComboSlotOptionCardDetail>
    </ComboSlotOptionWrapper>
  );
};

export default ComboSlotOption;
